import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { EditorDraft, readDraft, removeDraft, stableValue, writeDraft } from '../editorDraft';

export function useEditorDraft<T>({ storageKey, source, ready, value, restore, validate, busy = false }: {
  storageKey: string | null; source: string; ready: string | null; value: T;
  restore: (value: T) => void; validate: (value: unknown) => value is T;
  busy?: boolean;
}) {
  const [baseline, setBaseline] = useState<{ source: string; value: string } | null>(null);
  const [pending, setPending] = useState<EditorDraft<T> | null>(null);
  const [storageError, setStorageError] = useState(false);
  const [navigation, setNavigation] = useState<{ action: () => void } | null>(null);
  const [acknowledgedSource, setAcknowledgedSource] = useState<string | null>(null);
  const [reset, setReset] = useState(0);
  const loaded = useRef<string | null>(null);
  const capturedHydration = useRef<string | null>(null);
  const stopped = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const serialized = stableValue(value);
  const dirty = !!baseline && baseline.value !== serialized;
  const conflict = !!baseline && baseline.source !== source && acknowledgedSource !== source;
  const current = useRef({ storageKey, source, baseline, value, dirty, pending });
  current.current = { storageKey, source, baseline, value, dirty, pending };
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useLayoutEffect(() => {
    if (ready !== `${source}:${reset}` || capturedHydration.current === ready) return;
    capturedHydration.current = ready;
    setBaseline({ source, value: serialized });
  }, [ready, source, serialized, reset]);

  useEffect(() => {
    if (!storageKey || !baseline || loaded.current === storageKey) return;
    loaded.current = storageKey;
    try { setPending(readDraft(storageKey, validate)); }
    catch { setStorageError(true); }
  }, [storageKey, baseline, validate]);

  // Flush is synchronous, including on unmount/auth failure. It never calls the database.
  const flush = () => {
    clearTimeout(timer.current);
    const state = current.current;
    if (stopped.current || !state.storageKey || !state.baseline || !state.dirty || state.pending) return true;
    try { writeDraft(state.storageKey, state.baseline.source, state.value); return true; }
    catch { setStorageError(true); return false; }
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    if (dirty && !pending) timer.current = setTimeout(() => flushRef.current(), 500);
    return () => clearTimeout(timer.current);
  }, [serialized, dirty, pending]);

  useEffect(() => {
    const unload = (event: BeforeUnloadEvent) => {
      if ((!current.current.dirty && !busyRef.current) || stopped.current) return;
      flushRef.current(); event.preventDefault(); event.returnValue = '';
    };
    const hidden = () => { if (document.visibilityState === 'hidden') flushRef.current(); };
    const navigate = (event: Event) => {
      if (busyRef.current) { event.preventDefault(); return; }
      if (stopped.current || (!current.current.dirty && !current.current.pending)) return;
      event.preventDefault(); flushRef.current();
      setNavigation({ action: (event as CustomEvent).detail.action });
    };
    // Intercept ordinary same-tab links before the router sees them; CRM state-navigation uses the same event.
    const link = (event: MouseEvent) => {
      const anchor = (event.target as Element)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (busyRef.current) { event.preventDefault(); event.stopPropagation(); return; }
      if (!current.current.dirty || stopped.current || anchor.href === window.location.href) return;
      event.preventDefault(); event.stopPropagation(); flushRef.current();
      setNavigation({ action: () => window.location.assign(anchor.href) });
    };
    window.addEventListener('beforeunload', unload);
    window.addEventListener('pagehide', flushRef.current);
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('quikpix:editor-navigation', navigate);
    document.addEventListener('click', link, true);
    const pagehide = flushRef.current;
    return () => {
      flushRef.current();
      window.removeEventListener('beforeunload', unload);
      window.removeEventListener('pagehide', pagehide);
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('quikpix:editor-navigation', navigate);
      document.removeEventListener('click', link, true);
    };
  }, []);

  const clear = () => {
    clearTimeout(timer.current);
    if (storageKey) { try { removeDraft(storageKey); } catch { setStorageError(true); return false; } }
    stopped.current = true; setPending(null); return true;
  };
  const captureSave = () => ({ key: current.current.storageKey, source: current.current.source,
    baseline: current.current.baseline?.source, value: JSON.parse(stableValue(current.current.value)) as T });
  const saved = (submitted = captureSave(), assigned: Partial<T> = {}, savedSource = submitted.source) => {
    const state = current.current;
    // Only reconcile identities assigned by this Save, never newer field edits.
    const matches = (value: T) => {
      const normalized = { ...value };
      for (const key of Object.keys(assigned) as Array<keyof T>) {
        if (stableValue(normalized[key]) === stableValue(assigned[key])) normalized[key] = submitted.value[key];
      }
      return stableValue(normalized) === stableValue(submitted.value);
    };
    try {
      const stored = submitted.key ? readDraft(submitted.key, validate) : null;
      if (stored && (stored.baseline !== submitted.baseline || !matches(stored.value)) &&
          (stored.baseline !== state.baseline?.source || stableValue(stored.value) !== stableValue(state.value))) {
        // A different stored snapshot needs explicit recovery, not an unmount flush over it.
        clearTimeout(timer.current);
        current.current = { ...state, pending: stored };
        setPending(stored);
        return false;
      }
      if (state.storageKey !== submitted.key || state.pending || !matches(state.value) ||
          state.baseline?.source !== submitted.baseline ||
          (state.source !== submitted.source && state.source !== savedSource)) {
        flush(); return false;
      }
      if (stored && (stored.baseline !== submitted.baseline || !matches(stored.value))) return false;
      clearTimeout(timer.current);
      const next = { source: savedSource, value: stableValue(state.value) };
      // Synchronous protection against an unmount flush while React commits the acknowledgement.
      current.current = { ...state, baseline: next, dirty: false };
      setBaseline(next); setPending(null);
      if (submitted.key) removeDraft(submitted.key);
      return true;
    } catch { setStorageError(true); return false; }
  };
  return {
    dirty, pending, conflict: conflict || (!!pending && pending.baseline !== source), storageError, navigation, reset,
    canHydrate: !dirty && !pending,
    // Local storage failure must not prevent an explicit database Save from rescuing the work.
    blocked: !!pending || conflict,
    flush,
    captureSave, saved,
    resume: () => { if (pending) { setBaseline(previous => previous && ({ ...previous, source: pending.baseline })); restore(pending.value); setPending(null); } },
    acknowledge: () => { setAcknowledgedSource(source); setBaseline(previous => previous && ({ ...previous, source })); },
    discard: () => {
      if (clear()) { stopped.current = false; setBaseline(null); setStorageError(false); setAcknowledgedSource(null); setReset(n => n + 1); }
    },
    stay: () => setNavigation(null),
    leave: (discard: boolean) => {
      if (discard ? !clear() : !flush()) return;
      stopped.current = true;
      const action = navigation?.action; setNavigation(null); action?.();
    },
  };
}
